import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import api from "../Services/api";
import { stablesHttpClient } from "../Services/stablesApi";
import {
    clearAuthSession,
    clearTabSessionRevoked,
    isTabSessionRevoked,
    markTabSessionRevoked,
    persistAuthSessionJson,
    readAuthSessionJson,
    syncHttpAuthorization,
} from "../Services/authToken";
import { releaseSessionTabClaim } from "../Services/sessionTabClaim";
import create from 'zustand'

import { useToast } from "../Hooks/toast";
import { useHistory } from "react-router-dom";
import {
    buildMockFidoAccessToken,
    hasRegisteredFidoKey,
    isFidoPilotAccount,
    isMockFidoToken,
    normalizeApiTwoFactorMethods,
    resolveTwoFactorAccountKey,
    readTwoFactorRecord,
} from "../Constants/webauthnMock";
import type { WebAuthnTokenModel } from "../Services/webauthnApi";
import { isCarteiraReadOnly } from "../Services/carteiraUsersApi";
import { buildWebDevicePayload } from "../Services/deviceIdentity";
import {
    clearIbAccountPickPending,
    setIbAccountPickPending,
} from "../Constants/accountTypes";

const permissionIds = [
  "admin",
  "statementsview",
  "statementsbalanceview",
  "receiptsview",
  "refundsview",
  "stablesview",
  "stablesoperate",
  "internationalview",
  "internationaltransfer",
  "virtualaccountsview",
  "virtualaccountscreate",
  "pixview",
  "paypix",
  "receivepix",
  "pixkeycreate",
  "pixkeydelete",
  "boletocreateview",
  "payboleto",
  "createboleto",
  "p2pview",
  "transp2p",
  "operatorsview",
  "operatorscreate",
  "mycardview",
] as const;

export type PermissionId = typeof permissionIds[number];

export interface useAuthStoreProps {
    token: string
    username: string
    password: string
    accepted_terms: boolean
    phone_number: string
    two_factor_methods: string[]
    otp_sent_via?: string
    device_id?: number | null
    /** True when the person has WebAuthn credentials registered server-side. */
    has_web_authn?: boolean
    /** Dev-only OTP hint from BaaS — surfaced as a convenience regardless of delivery status. */
    dev_otp_code?: string | null
    /** True when BaaS actually delivered the OTP via SMS/WhatsApp. Drives the FE "disabled" hint. */
    otp_delivered?: boolean
    isAutenticated: boolean,
    expireDate: string
    sessionTimeout: number
    permissions: PermissionId[]
    /** When false, wallet (Carteira) is disabled — user stays in /baas console. Default true. */
    canAccessCarteira: boolean
    canAccessConsole: boolean
    setUser: (props: { username: string, phone_number: string, password: string, two_factor_methods?: string[], otp_sent_via?: string, device_id?: number | null, has_web_authn?: boolean, dev_otp_code?: string | null, otp_delivered?: boolean }) => void
    setAutenticated: (props: {
        isAutenticated: boolean
        accepted_terms: boolean
        token: string
        expireDate: string
        sessionTimeout: number
        permissions: PermissionId[]
        canAccessCarteira?: boolean
        canAccessConsole?: boolean
    }) => void
    setAcceptedTerms: (props: { accepted_terms: boolean }) => void
    removeUser: () => void
    setSessionTimeout: (props: { sessionTimeout: number }) => void
}

export const SESSION_DISCONNECTED_KEY = '@WalletBC:sessionDisconnected';

const getAuthErrorToast = (err: unknown, fallbackTitle = 'Erro ao autenticar') => ({
    title: (err as { response?: { data?: { information?: string } } })?.response?.data?.information ?? fallbackTitle,
    description: (err as { response?: { data?: { description?: string } } })?.response?.data?.description,
});

const otpChannelLabel = (otpSentVia?: string | null): string => {
    const normalized = (otpSentVia || "").toLowerCase().trim();
    if (normalized === "whatsapp") return "WhatsApp";
    if (normalized === "email") return "e-mail";
    return "SMS";
};

const otpDeliveredToastDescription = (
    otpSentVia?: string | null,
    { resent = false }: { resent?: boolean } = {}
): string => {
    const channel = otpChannelLabel(otpSentVia);
    const destination =
        (otpSentVia || "").toLowerCase().trim() === "email"
            ? "e-mail cadastrado"
            : "telefone cadastrado";
    return resent
        ? `Enviamos um novo código por ${channel} para o ${destination}.`
        : `Enviamos um código por ${channel} para o ${destination}.`;
};

const otpDeliveryFailureDescription = (
    otpSentVia?: string | null,
    detail?: string | null
): string => {
    const channel = otpChannelLabel(otpSentVia);
    const trimmed = typeof detail === "string" ? detail.trim() : "";
    if (trimmed) {
        return `Não foi possível enviar o código por ${channel}: ${trimmed}`;
    }
    return `Não foi possível enviar o código por ${channel}. Verifique o e-mail cadastrado e a configuração SMTP/SendGrid da instância, ou tente outro método.`;
};

const clearApiAuthorization = () => {
    syncHttpAuthorization(api, "");
    syncHttpAuthorization(stablesHttpClient, "");
};

const syncApiAuthorization = (token: string) => {
    syncHttpAuthorization(api, token);
    syncHttpAuthorization(stablesHttpClient, token);
};

const parseSessionExpiry = (
    expires_in: string | number
): { expireDate: string; sessionTimeout: number } => {
    const now = Date.now();
    const raw = expires_in;
    const numeric =
        typeof raw === "number" ? raw : Number(String(raw).trim());

    if (!Number.isNaN(numeric) && String(raw).trim() !== "") {
        // OAuth-style lifetime in seconds (e.g. 3600, 28800)
        if (numeric > 0 && numeric < 86400 * 30) {
            const expire = new Date(now + numeric * 1000);
            return {
                expireDate: expire.toISOString(),
                sessionTimeout: Math.floor(numeric),
            };
        }

        // Unix timestamp in seconds
        if (numeric >= 1e9 && numeric < 1e12) {
            const expire = new Date(numeric * 1000);
            const sessionTimeout = Math.max(
                0,
                Math.floor((expire.getTime() - now) / 1000)
            );
            return { expireDate: expire.toISOString(), sessionTimeout };
        }

        // Unix timestamp in milliseconds
        if (numeric >= 1e12) {
            const expire = new Date(numeric);
            const sessionTimeout = Math.max(
                0,
                Math.floor((expire.getTime() - now) / 1000)
            );
            return { expireDate: expire.toISOString(), sessionTimeout };
        }
    }

    const parsed = new Date(String(raw));
    if (!Number.isNaN(parsed.getTime())) {
        const sessionTimeout = Math.max(
            0,
            Math.floor((parsed.getTime() - now) / 1000)
        );
        return { expireDate: parsed.toISOString(), sessionTimeout };
    }

    const fallbackSeconds = 8 * 60 * 60;
    const expire = new Date(now + fallbackSeconds * 1000);
    return {
        expireDate: expire.toISOString(),
        sessionTimeout: fallbackSeconds,
    };
};

const refreshSessionTimeout = (stored: typeof defaultUser) => {
    if (!stored?.expireDate) return stored;
    const expireMs = new Date(stored.expireDate).getTime();
    if (Number.isNaN(expireMs)) return stored;
    return {
        ...stored,
        sessionTimeout: Math.max(0, Math.floor((expireMs - Date.now()) / 1000)),
    };
};

const sanitizePersistedSession = (stored: typeof defaultUser) => {
    const withAccessDefaults = refreshSessionTimeout({
        ...stored,
        canAccessCarteira: stored.canAccessCarteira !== false,
        canAccessConsole: stored.canAccessConsole === true,
    });

    if (!isMockFidoToken(withAccessDefaults.token)) return withAccessDefaults;

    return {
        ...defaultUser,
        username: withAccessDefaults.username || '',
        phone_number: withAccessDefaults.phone_number || '',
    };
};

const defaultUser = {
    token: '',
    username: '',
    password: '',
    phone_number: '',
    two_factor_methods: [] as string[],
    otp_sent_via: '',
    device_id: null as number | null,
    has_web_authn: false,
    dev_otp_code: null as string | null,
    otp_delivered: false,
    accepted_terms: false,
    isAutenticated: false,
    expireDate: '',
    sessionTimeout: 0,
    permissions: [] as PermissionId[],
    canAccessCarteira: true,
    canAccessConsole: false,
}

/** Post-login landing: console-only persons go straight to /baas. */
export const resolvePostLoginPath = (data: {
    can_access_carteira?: boolean
    canAccessCarteira?: boolean
    can_access_console?: boolean
    canAccessConsole?: boolean
}) => {
    const canAccessCarteira = data.can_access_carteira ?? data.canAccessCarteira ?? true;
    const canAccessConsole = data.can_access_console ?? data.canAccessConsole ?? false;
    if (!canAccessCarteira && canAccessConsole) return "/baas";
    if (!canAccessCarteira) return "/baas";
    return "/";
};

export const useAuthStore = create<useAuthStoreProps>(setState => {
    let user = { ...defaultUser }

    const userJson = readAuthSessionJson();

    if (userJson) {
        user = sanitizePersistedSession(JSON.parse(userJson));
    }

    return {
        ...user,
        setUser({ username = '', phone_number = '', password = '', two_factor_methods = [], otp_sent_via = '', device_id = null, has_web_authn = false, dev_otp_code = null, otp_delivered = false }) {
            clearApiAuthorization();
            setState(() => ({
                username,
                phone_number,
                password,
                two_factor_methods,
                otp_sent_via,
                device_id,
                has_web_authn,
                dev_otp_code,
                otp_delivered,
                isAutenticated: false,
                token: '',
                expireDate: '',
                sessionTimeout: 0,
                permissions: [],
                canAccessCarteira: true,
                canAccessConsole: false,
            }));
        },
        setAutenticated({
            isAutenticated = false,
            accepted_terms = false,
            token = '',
            expireDate = '',
            sessionTimeout,
            permissions,
            canAccessCarteira = true,
            canAccessConsole = false,
        }) {
            setState(() => ({
                isAutenticated,
                accepted_terms,
                token,
                expireDate,
                sessionTimeout,
                permissions,
                canAccessCarteira,
                canAccessConsole,
            }));
        },
        setAcceptedTerms({ accepted_terms = false }) {            
            setState(() => ({ accepted_terms }));
        },
        removeUser() {
            clearApiAuthorization();
            setState(() => ({ ...defaultUser }));
        },
        setSessionTimeout({ sessionTimeout = 0 }) {
            setState(() => ({ sessionTimeout }));
        },
    }
})

syncApiAuthorization(useAuthStore.getState().token);

useAuthStore.subscribe((state, prevState) => {
    const { password, ...persisted } = state;
    const json = JSON.stringify(persisted);

    persistAuthSessionJson(json, { browser: false });

    if (
        state.isAutenticated &&
        state.token &&
        !isTabSessionRevoked()
    ) {
        const authFingerprintChanged =
            state.token !== prevState.token ||
            state.isAutenticated !== prevState.isAutenticated ||
            state.expireDate !== prevState.expireDate ||
            state.accepted_terms !== prevState.accepted_terms ||
            state.username !== prevState.username ||
            state.canAccessCarteira !== prevState.canAccessCarteira ||
            state.canAccessConsole !== prevState.canAccessConsole;

        if (authFingerprintChanged) {
            persistAuthSessionJson(json, { browser: true });
        }
    }

    if (state.token !== prevState.token) {
        syncApiAuthorization(state.token);
    }
});

export type OtpDeliveryNotice = {
    title: string
    description: string
    /** Shared inline slot: info = code sent; warning = OTP timer expired. */
    variant?: "info" | "warning"
}

type OtpDeliveryNoticeStore = {
    otpDeliveryNotice: OtpDeliveryNotice | null
    setOtpDeliveryNotice: (notice: OtpDeliveryNotice | null) => void
}

/** Transient UI notice — separate from session store so it is not persisted. */
const useOtpDeliveryNoticeStore = create<OtpDeliveryNoticeStore>((set) => ({
    otpDeliveryNotice: null,
    setOtpDeliveryNotice: (otpDeliveryNotice) => set({ otpDeliveryNotice }),
}))

export const useAuth = () => {

    const { setAcceptedTerms, setAutenticated, setUser, removeUser, setSessionTimeout, ...user } = useAuthStore();
    const otpDeliveryNotice = useOtpDeliveryNoticeStore((state) => state.otpDeliveryNotice);
    const setOtpDeliveryNotice = useOtpDeliveryNoticeStore((state) => state.setOtpDeliveryNotice);

    const { addToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    const history = useHistory();

    const timeoutRef = useRef(null);

    const clearOtpDeliveryNotice = useCallback(() => {
        setOtpDeliveryNotice(null);
    }, [setOtpDeliveryNotice]);

    const clearOtpExpiredNotice = useCallback(() => {
        const current = useOtpDeliveryNoticeStore.getState().otpDeliveryNotice;
        if (current?.variant === "warning") {
            setOtpDeliveryNotice(null);
        }
    }, [setOtpDeliveryNotice]);

    const showOtpExpiredNotice = useCallback(() => {
        setOtpDeliveryNotice({
            title: "Token expirado",
            description: "O token expirou, clique em reenviar para renová-lo.",
            variant: "warning",
        });
    }, [setOtpDeliveryNotice]);

    const formattedTimestamp = useMemo(() => {
    
        let totalSeconds = user.sessionTimeout;
    
        let hours = Math.floor(totalSeconds / 3600);
        totalSeconds %= 3600;
    
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds % 60);
    
        return [
          hours.toString().padStart(2, '0'),
          minutes.toString().padStart(2, '0'),
          seconds.toString().padStart(2, '0'),
        ].join(':');
      }, [user.sessionTimeout])

    useEffect(() => {
        if(user.sessionTimeout < 1) return clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            setSessionTimeout({ sessionTimeout: user.sessionTimeout - 1 });
        }, 1000);

        return () => clearTimeout(timeoutRef.current);
    }, [user.sessionTimeout])

    const completeMobileLogin = (data: {
        access_token?: string;
        expires_in?: string | number;
        accepted_terms?: boolean;
        permissions?: PermissionId[];
        can_access_carteira?: boolean;
        can_access_console?: boolean;
    }) => {
        const accessToken = data.access_token;
        if (!accessToken) {
            addToast({
                title: "Falha na autenticação",
                description: "Resposta inválida do servidor.",
                type: "error",
            });
            return false;
        }

        const { expireDate, sessionTimeout } = parseSessionExpiry(
            data.expires_in ?? 8 * 60 * 60
        );

        const canAccessCarteira = data.can_access_carteira ?? true;
        const canAccessConsole = data.can_access_console ?? false;

        clearTabSessionRevoked();
        syncApiAuthorization(accessToken);

        setOtpDeliveryNotice(null);

        setAutenticated({
            accepted_terms: data.accepted_terms ?? true,
            token: accessToken,
            isAutenticated: true,
            expireDate,
            sessionTimeout,
            permissions: (data.permissions ?? (canAccessCarteira ? ["admin"] : ["statementsview"])) as PermissionId[],
            canAccessCarteira,
            canAccessConsole,
        });

        if (!(data.accepted_terms ?? true)) {
            history.push("/auth/accepted-terms");
            return true;
        }

        // Carteira multi-account: show Conta card picker after login.
        if (canAccessCarteira) {
            setIbAccountPickPending(true);
        }

        history.push(resolvePostLoginPath({ canAccessCarteira, canAccessConsole }));
        return true;
    };

    const auth = async ({ username, password }) => {
        setIsLoading(true);
        try {
            clearApiAuthorization();
            // Challenge only the principal OTP channel on first login (never sms+whatsapp).
            // Prefer local 2FA preference so UI principal matches the dispatched channel;
            // BaaS still falls back to person.PreferredTwoFactorChannel when this is omitted.
            const accountKey = resolveTwoFactorAccountKey({ username });
            const preferredLogin = readTwoFactorRecord(accountKey).preferredLogin;
            const principalOtp =
                preferredLogin === "sms"
                || preferredLogin === "whatsapp"
                || preferredLogin === "email"
                    ? preferredLogin
                    : undefined;

            const { data } = await api.post('/v1/auth/login', {
                username,
                password,
                ...(principalOtp ? { verification_method: principalOtp } : {}),
                device: buildWebDevicePayload(),
            });

            // BaaS: known device → JWT immediately
            if (data?.access_token && !data?.require_verification) {
                setUser({
                    phone_number: data.phone_number || '',
                    password,
                    username,
                    two_factor_methods: normalizeApiTwoFactorMethods(
                        data.two_factor_methods || data.verification_methods || []
                    ),
                    device_id: data.device_id ?? null,
                    has_web_authn: data.has_web_authn === true,
                });
                completeMobileLogin(data);
                return;
            }

            // New / unverified device → OTP step
            const twoFactorMethods = Array.isArray(data.two_factor_methods) && data.two_factor_methods.length > 0
                ? normalizeApiTwoFactorMethods(data.two_factor_methods)
                : Array.isArray(data.verification_methods) && data.verification_methods.length > 0
                    ? normalizeApiTwoFactorMethods(data.verification_methods)
                    : ["email"];

            const otpSentVia = typeof data.otp_sent_via === "string"
                ? data.otp_sent_via
                : "";

            // Dev-only: BaaS surfaces the OTP here as a convenience so login can still be
            // completed without a real Google Authenticator enrollment against SecretTwoAuth.
            // otpDelivered says whether SMS/WhatsApp was actually sent — devOtpCode being set
            // does NOT by itself mean delivery is disabled (real credentials may be configured).
            const devOtpCode = typeof data.dev_otp_code === "string" && data.dev_otp_code
                ? data.dev_otp_code
                : null;
            const otpDelivered = data.otp_delivered === true;
            const otpDeliveryDetail = typeof data.otp_delivery_detail === "string"
                ? data.otp_delivery_detail
                : null;

            setUser({
                phone_number: data.phone_number || '',
                password,
                username,
                two_factor_methods: twoFactorMethods,
                otp_sent_via: otpSentVia,
                device_id: typeof data.device_id === "number" ? data.device_id : null,
                has_web_authn: data.has_web_authn === true,
                // Hide DEV code entirely when a real channel delivered the OTP.
                dev_otp_code: otpDelivered ? null : devOtpCode,
                otp_delivered: otpDelivered,
            });

            // Always replace prior notice (e.g. stale "Token expirado" from a previous attempt).
            if (otpDelivered) {
                setOtpDeliveryNotice({
                    title: "Código enviado",
                    description: otpDeliveredToastDescription(otpSentVia),
                    variant: "info",
                });
            } else {
                setOtpDeliveryNotice({
                    title: "Falha no envio do código",
                    description: otpDeliveryFailureDescription(otpSentVia, otpDeliveryDetail),
                    variant: "warning",
                });
                if (devOtpCode) {
                    addToast({
                        title: `[DEV] Código OTP: ${devOtpCode}`,
                        description: `Não foi possível enviar o código por ${otpChannelLabel(otpSentVia)} — use este código para concluir o login.`,
                        type: "info",
                    });
                } else {
                    addToast({
                        title: "Código não enviado",
                        description: otpDeliveryFailureDescription(otpSentVia, otpDeliveryDetail),
                        type: "error",
                    });
                }
            }

            return history.push('/auth/verification-code');
        }
        catch(err) {
            const { title, description } = getAuthErrorToast(err);
            addToast({
                title,
                description,
                type: 'error'
            });
        }
        finally {
            setIsLoading(false);
        }
    }

    const completeWebAuthnLogin = async (auth: WebAuthnTokenModel): Promise<boolean> => {
        const accessToken = auth.access_token;
        if (!accessToken) {
            addToast({
                title: "Falha na autenticação",
                description: "Resposta inválida do servidor WebAuthn.",
                type: "error",
            });
            return false;
        }

        setIsLoading(true);

        try {
            // Same session completion path as OTP verify — full permissions / product flags.
            return completeMobileLogin({
                access_token: accessToken,
                expires_in: auth.expires_in,
                accepted_terms: auth.accepted_terms,
                permissions: auth.permissions as PermissionId[] | undefined,
                can_access_carteira: auth.can_access_carteira,
                can_access_console: auth.can_access_console,
            });
        } catch (err) {
            const { title, description } = getAuthErrorToast(
                err,
                'Falha na autenticação'
            );
            addToast({
                title,
                description: description ?? 'Não foi possível concluir o login com a chave USB.',
                type: "error",
            });
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const twoFactorFidoMock = async (): Promise<boolean> => {
        const accountKey = resolveTwoFactorAccountKey({ username: user.username });

        if (!isFidoPilotAccount(user.username)) {
            addToast({
                title: "Método indisponível",
                description: "Autenticação por chave USB não está habilitada para esta conta.",
                type: "error",
            });
            return false;
        }

        if (!hasRegisteredFidoKey(accountKey)) {
            addToast({
                title: "Chave não cadastrada",
                description:
                    "Nenhuma chave USB encontrada para este documento neste dispositivo. Use SMS ou cadastre a chave em Configurações.",
                type: "error",
            });
            return false;
        }

        setIsLoading(true);

        try {
            const mockToken = buildMockFidoAccessToken(user.username);
            const expireDate = new Date(Date.now() + 8 * 60 * 60 * 1000);
            const permissions: PermissionId[] = ["admin"];

            clearTabSessionRevoked();
            syncApiAuthorization(mockToken);

            setAutenticated({
                accepted_terms: true,
                token: mockToken,
                isAutenticated: true,
                expireDate: expireDate.toISOString(),
                sessionTimeout: 8 * 60 * 60,
                permissions,
            });

            history.push('/');
            return true;
        } catch (err) {
            const { title, description } = getAuthErrorToast(
                err,
                'Falha na autenticação'
            );
            addToast({
                title,
                description: description ?? 'Não foi possível concluir o login com a chave USB.',
                type: "error",
            });
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    const getPilotTwoFactorPreferences = useCallback(() => {
        const accountKey = resolveTwoFactorAccountKey({ username: user.username });
        const record = readTwoFactorRecord(accountKey);

        return {
            isPilot: isFidoPilotAccount(user.username),
            hasFidoKey: hasRegisteredFidoKey(accountKey),
            preferredLogin: record.preferredLogin,
        };
    }, [user.username]);

    const twoFactor = async ({ code = '' }) => {
        setIsLoading(true);

        try {
            const deviceId = user.device_id;
            const { data } = await api.post('/v1/auth/verify', {
                code,
                verification_code: code,
                device_id: deviceId,
                username: user.username,
                national_registration: user.username,
            });

            completeMobileLogin({
                access_token: data.access_token,
                accepted_terms: data.accepted_terms,
                expires_in: data.expires_in,
                permissions: data.permissions,
                can_access_carteira: data.can_access_carteira,
                can_access_console: data.can_access_console,
            });
        }
        catch(err) {
            const { title, description } = getAuthErrorToast(err);
            addToast({
                title,
                description,
                type: 'error'
            });
        }
        finally {
            setIsLoading(false);
        }
    }

    const acceptedTerms = async ({ accepted = true }) => {
        setIsLoading(true);
        try {
            // IB v2: terms acceptance is local for now (BaaS login defaults accepted_terms=true).
            // Revisit when a BaaS terms endpoint exists — do not call api-carteira.
            setAcceptedTerms({ accepted_terms: accepted });
        }
        catch(err) {
            const { title, description } = getAuthErrorToast(err);
            addToast({
                title,
                description,
                type: 'error'
            });
        }
        finally {
            setIsLoading(false);
        }
    }

    const sendVerificationCode = useCallback(async ({ username = '', password = '', verification_method = '' }): Promise<boolean> => {
        try {
            clearApiAuthorization();
            const { data } = await api.post('/v1/auth/login', {
                username,
                password,
                verification_method,
                device: buildWebDevicePayload(),
            });

            // Dev-only: refresh the visible OTP hint — resending invalidates the previous
            // challenge, so the earlier code (toast/banner) is no longer valid.
            const otpSentVia = typeof data?.otp_sent_via === "string"
                ? data.otp_sent_via
                : verification_method || user.otp_sent_via || "";
            const devOtpCode = typeof data?.dev_otp_code === "string" && data.dev_otp_code
                ? data.dev_otp_code
                : null;
            const otpDelivered = data?.otp_delivered === true;
            const otpDeliveryDetail = typeof data?.otp_delivery_detail === "string"
                ? data.otp_delivery_detail
                : null;
            setUser({
                ...user,
                otp_sent_via: otpSentVia,
                // When real delivery worked, never keep a DEV code in UI state.
                dev_otp_code: otpDelivered ? null : devOtpCode,
                otp_delivered: otpDelivered,
            });
            // New challenge: drop any expired/stale banner, then show delivery info when applicable.
            if (otpDelivered) {
                setOtpDeliveryNotice({
                    title: "Código enviado",
                    description: otpDeliveredToastDescription(otpSentVia, { resent: true }),
                    variant: "info",
                });
            } else {
                setOtpDeliveryNotice({
                    title: "Falha no envio do código",
                    description: otpDeliveryFailureDescription(otpSentVia, otpDeliveryDetail),
                    variant: "warning",
                });
                if (devOtpCode) {
                    addToast({
                        title: `[DEV] Código OTP: ${devOtpCode}`,
                        description: `Não foi possível enviar o código por ${otpChannelLabel(otpSentVia)} — use este código para concluir o login.`,
                        type: "info",
                    });
                } else {
                    addToast({
                        title: "Código não enviado",
                        description: otpDeliveryFailureDescription(otpSentVia, otpDeliveryDetail),
                        type: "error",
                    });
                }
            }

            // API accepted the challenge request — delivery outcome is surfaced above.
            return true;
        } catch {
            return false;
        }
    }, [addToast, setOtpDeliveryNotice, setUser, user]);

    const resendInFlightRef = useRef(false);

    const resendCode = async (verification_method = '') => {
        // Manual resend only — ignore overlapping clicks / accidental double-fire.
        if (resendInFlightRef.current || isLoading) return;
        resendInFlightRef.current = true;
        setIsLoading(true);
        try {
            const success = await sendVerificationCode({
                username: user.username,
                password: user.password,
                verification_method,
            });
            if (!success) {
                addToast({
                    title: 'Erro ao reenviar código',
                    description: 'Não foi possível reenviar o código. Tente novamente.',
                    type: 'error',
                });
            }
        } finally {
            setIsLoading(false);
            resendInFlightRef.current = false;
        }
    }

    const logout = useCallback(({
        disconnected = false,
        localOnly = false,
        clearBrowser,
    }: {
        disconnected?: boolean
        /** Skip POST /auth/logout — server session must stay valid (tab claim / already replaced). */
        localOnly?: boolean
        /** Override localStorage wipe. Default: wipe unless localOnly (tab claim keeps the JWT). */
        clearBrowser?: boolean
    } = {}) => {
        const token = useAuthStore.getState().token;
        const wipeBrowser = clearBrowser ?? !localOnly;

        if (!localOnly && token && !isMockFidoToken(token)) {
            api.post("/v1/auth/logout", null, {
                headers: { Authorization: `Bearer ${token}` },
            }).catch(() => undefined);
        }

        if (localOnly) {
            markTabSessionRevoked();
        } else {
            clearTabSessionRevoked();
        }

        if (wipeBrowser) {
            releaseSessionTabClaim();
        }

        clearTimeout(timeoutRef.current);
        clearApiAuthorization();
        clearAuthSession({ browser: wipeBrowser });
        clearIbAccountPickPending();
        setOtpDeliveryNotice(null);
        removeUser();

        if (disconnected) {
            sessionStorage.setItem(SESSION_DISCONNECTED_KEY, '1');
            return history.push({
                pathname: '/auth',
                search: '?session=expired',
                state: { sessionDisconnected: true },
            });
        }

        return history.push('/auth');
    }, [history, removeUser, setOtpDeliveryNotice])

    const refleshToken = async () => {

        addToast({
            title: "Sua sessão foi renovada!",
            description: "Sessão renovada com sucesso.",
            type: "info",
        });
    }

    const hasPermission = (permission: PermissionId) => {

        return user.permissions.includes('admin') || user.permissions.includes(permission);
    }

    const isReadOnly = isCarteiraReadOnly(user?.permissions);

    return {
        auth,
        twoFactor,
        twoFactorFidoMock,
        completeWebAuthnLogin,
        getPilotTwoFactorPreferences,
        acceptedTerms,
        sendVerificationCode,
        resendCode,
        logout,
        refleshToken,
        isLoading,
        formattedTimestamp,
        user,
        hasPermission,
        /** Viewer / visualizador — see everything, mutate nothing. */
        isReadOnly,
        otpDeliveryNotice,
        clearOtpDeliveryNotice,
        clearOtpExpiredNotice,
        showOtpExpiredNotice,
    }
}


